'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Image } from '@/components/ui/image';
import { productPlaceholder } from '@/lib/placeholders';
import {
  getDisplayImageUrl,
  resolveProductImageSrc,
  shouldOptimizeNextImage,
  type ProductImageSrc,
} from '@/lib/optimizable-image-url';
import cn from 'classnames';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DESKTOP_ZOOM = 2.5;

interface ProductImageZoomProps {
  src?: ProductImageSrc | null;
  alt: string;
  width: number;
  height: number;
  className?: string;
  /** Set for LCP image (first product image) to disable lazy load and use fetchpriority=high */
  priority?: boolean;
  /** Responsive sizes for optimized delivery (e.g. "(max-width: 768px) 100vw, 450px") */
  sizes?: string;
}

const MAIN_GALLERY_SIZES = '(max-width: 768px) 100vw, 450px';

export function ProductImageZoom({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = MAIN_GALLERY_SIZES,
}: Readonly<ProductImageZoomProps>) {
  const imageSrc = resolveProductImageSrc(src, productPlaceholder);
  const displayUrl = getDisplayImageUrl(imageSrc);
  const unoptimized = !shouldOptimizeNextImage(imageSrc);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(MIN_SCALE);

  const [hover, setHover] = useState({
    active: false,
    x: 0,
    y: 0,
  });

  // Resolve after mount to avoid hydration mismatch: server has no window,
  // so we always render mobile branch until client hydrates, then switch if desktop.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    setIsDesktop(
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    );
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = imageWrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    setHover({
      active: true,
      x,
      y,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHover((prev) => ({ ...prev, active: false }));
  }, []);

  const handleTransformed = useCallback((_ref: unknown, state: { scale: number }) => {
    setScale(state.scale);
  }, []);

  /*
  =========================
  Desktop Hover Zoom
  =========================
  */
  if (isDesktop) {
    const translateX = hover.x * 100;
    const translateY = hover.y * 100;

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative w-full h-full flex items-center justify-center overflow-hidden rounded-xl cursor-zoom-in',
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Wrapper sized like the visible image (object-contain) so zoom aligns with image edges */}
        <div
          ref={imageWrapperRef}
          className="relative inline-block max-w-full max-h-full"
        >
          {/* Base Image */}
          <Image
            unoptimized={unoptimized}
            src={imageSrc}
            alt={alt}
            width={width}
            height={height}
            sizes={sizes}
            priority={priority}
            draggable={false}
            className="block max-w-full max-h-full object-contain select-none"
          />

          {/* Zoom Overlay - raw img for dynamic zoom transform; not LCP */}
          <div
            className={cn(
              'absolute inset-0 overflow-hidden rounded-xl transition-opacity duration-200 ease-out pointer-events-none',
              hover.active ? 'opacity-100' : 'opacity-0'
            )}
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- zoom overlay needs dynamic style, not LCP */}
            <img
              src={displayUrl}
              alt=""
              aria-hidden
              width={width}
              height={height}
              draggable={false}
              className="absolute max-w-none select-none object-cover"
              style={{
                width: `${DESKTOP_ZOOM * 100}%`,
                height: `${DESKTOP_ZOOM * 100}%`,
                left: `-${translateX * (DESKTOP_ZOOM - 1)}%`,
                top: `-${translateY * (DESKTOP_ZOOM - 1)}%`,
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  /*
  =========================
  Mobile Pinch Zoom
  =========================
  */
  return (
    <div
      className={cn(
        'w-full h-full overflow-hidden flex items-center justify-center',
        className
      )}
    >
      <TransformWrapper
        minScale={MIN_SCALE}
        maxScale={MAX_SCALE}
        initialScale={1}
        centerOnInit
        limitToBounds
        wheel={{ disabled: true }}
        doubleClick={{ disabled: true }}
        pinch={{ disabled: false }}
        panning={{
          disabled: scale <= 1,
          velocityDisabled: true,
        }}
        onTransformed={handleTransformed}
      >
        <TransformComponent
          wrapperStyle={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          contentStyle={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Image
            unoptimized={unoptimized}
            src={imageSrc}
            alt={alt}
            width={width}
            height={height}
            sizes={sizes}
            priority={priority}
            draggable={false}
            className="max-w-full max-h-full object-contain select-none"
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}